import { sum, map } from "lodash";
import axios from "src/utils/axios";
import { createSlice } from "@reduxjs/toolkit";
import { mesaKicked } from "src/redux/actions/mesaKick";
import { batch } from "react-redux";
import { staleGuard } from "src/utils/staleGuard";
import {
  isTheSameProduct,
  isTheSameProductWithComplements,
} from "src/utils/utils";
import { optimisticSetComensal, getOrdersSuccess } from "./order";
import { getMesas } from "./mesas";
import { markLocalDinerSplitAction } from "src/utils/localCheckoutFetchTracker";

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  createdSuccess: false,
  updatedSuccess: false,
  error: false,
  menu: null,
  menus: [],
  products: [],
  frequentProducts: [],
  copas: [],
  product: null,
  sortBy: null,
  selectedComplemento: null,
  selectedIdComplemento: -1,
  selectedProduct: -1,
  selectedSubProduct: -1,
  unidadesSelectedProduct: -1,
  openComplementos: false,
  selectedCopa: null,
  categoryCreateOrder: [],
  selectedPage: 0,
  productPerPage: 5,
  lastTimeRequest: null,
  unidadesCopa: -1,
  filters: {
    category: [],
    priceRange: [0, 100],
    alergenos: [],
    bebida: "All",
    productName: "All",
  },
  checkout: {
    activeStep: 0,
    cart: [],
    oldCart: [],
    subtotal: 0,
    total: 0,
    discount: 0,
    shipping: 0,
    billing: null,
    idOrder: null,
    isPay: null,
    buttonPayClicked: null,
    // 2026-09-03 · Split-by-comensal. Diccionario `numero → subtotal`
    // que el backend rellena en GetCart tras cada refetch. La barra de
    // comensales lo consume directamente para pintar los badges sin
    // recalcular sumando líneas.
    subtotalPorComensal: {},
  },
  // 2026-09-03 · Split-by-comensal (Hiopos-like). Estado local del
  // camarero — no persiste en backend. Ver DinerSplitController para
  // el catálogo persistente (DataSession.ComensalesJson).
  dinerSplit: {
    // Comensal activo del TPV — todo producto nuevo que se añada al
    // cart se asigna a este número. `null` = no hay tab activo (los
    // productos van a C0 "sin asignar" hasta que el camarero seleccione
    // uno o los mueva a mano).
    activeComensal: null,
    // Lista cacheada de comensales de la sesión actual (viene del
    // backend). `{numero, nombre?}[]`. El color se deriva del número.
    comensales: [],
  },
  checkoutHistory: [],
  complementSelection: {
    cart: [],
    complementos: [],
    total: 0,
    steps: [],
    currentStep: 0,
    isComplete: false,
    selectedProduct: -1,
    isLoad: false,
  },
  productoNota: [],
  isNotasOpen: false,
  nota_temp: "",
  images: [
    {
      name: "",
      data: "",
    },
  ],
  selectedProductoCart: null,
  haveComplemento: false,
  complementos: [],
  page: 0,
  filterName: "",
  productCategoryFilter: [], // persisted array of category ids
  checkoutEdit: {
    activeStep: 0,
    cart: [],
    oldCart: [],
    subtotal: 0,
    total: 0,
    discount: 0,
    shipping: 0,
    billing: null,
    idOrder: null,
    isPay: null,
    buttonPayClicked: null,
  },
  checkoutToDelete: [],
  deleteMode: false,
  isLoadingPayment: false,
  partialPayment: null,
  onEditComplementProduct: null,
  lastCategorySelectedWhenCreate: null,
  complementosValues: [],
};


// Fase 3A · Normalización del cart tras añadir/sincronizar. Equivale a lo
// que se hacía antes:
//   cart.filter(q > 0).sort((p) => !p.servido && !p.old)
//       .map((p) => ({ ...p, unidades: 0, unidadesPagadas: 0 }))
// - El `.sort` con comparador de UN argumento nunca devuelve < 0, así que en
//   V8 (TimSort) era un no-op que conservaba el orden. Lo quitamos: mismo
//   orden, y determinista en cualquier motor.
// - Solo se crea un objeto nuevo si la línea no estaba ya normalizada: el
//   resto conserva su identidad y los componentes memoizados no repintan.
export function normalizeCartLines(cart) {
  const out = [];
  for (const p of cart || []) {
    if (!(p.quantity > 0)) continue;
    out.push(
      p.unidades === 0 && p.unidadesPagadas === 0
        ? p
        : { ...p, unidades: 0, unidadesPagadas: 0 },
    );
  }
  return out;
}

// Fase 3A · lógica de `addCart` extraída para reutilizarla en `addCartMany`
// (N productos en un único dispatch = un único render).
function addProductToCart(state, payload) {
  const action = { payload };
  let productoElement = action.payload;

  let product = { ...productoElement };

  if (!product?.unidades) product.unidades = product?.quantity;
  if (!product?.unidadesPagadas) product.unidadesPagadas = 0;

  // 2026-09-03 · Split-by-comensal. Si hay tab de comensal activo,
  // el nuevo producto se asigna a ese comensal — es la intención
  // explícita del camarero. Si NO hay tab activo (activeComensal
  // === null, tab "Todos"), se respeta el `product.idComensal`
  // del payload (útil para reasignación desde cart o casos donde
  // el caller ya sabe a quién asignar).
  // Bug fix 2026-09-03: antes solo se sobreescribía cuando
  // `product.idComensal == null`, pero el DTO viene del catálogo
  // con `idComensal: 0` (default C#), así que la condición nunca
  // se cumplía y el activeComensal se ignoraba.
  const active = state.dinerSplit?.activeComensal;
  if (active != null) {
    product.idComensal = active;
  } else if (product.idComensal == null) {
    product.idComensal = 0;
  }

  const isEmptyCart = state.checkout.cart.length === 0;
  var existed = false;

  if (isEmptyCart) {
    state.checkout.cart = [...state.checkout.cart, product];
  } else {
    state.checkout.cart = map(state.checkout.cart, (_product) => {
      var isExisted = isTheSameProduct(product, _product);

      if (isExisted) {
        existed = true;
        var subtotal =
          (_product.quantity + product.quantity) * _product.precio;
        return {
          ..._product,
          notas: product.notas,
          tag: product.tag,
          quantity: _product.quantity + product.quantity,
          subtotal: subtotal,
        };
      }
      return _product;
    });
  }

  if (!existed && !isEmptyCart) {
    state.checkout.cart = [product, ...state.checkout.cart];
  }

  state.checkout.cart = normalizeCartLines(state.checkout.cart);

  const subtotal = sum(
    state.checkout.cart.map((product) => product.precio * product.quantity),
  );
  state.checkout.subtotal = subtotal;
  state.checkout.total = subtotal;
}

// Fase 3A · lógica de `syncCartCheckout` reutilizable por
// `syncCartCheckoutMany` (SyncCart: un dispatch para todas las líneas).
// Impresas de dos líneas agrupadas. Sin el campo en ninguna (API antigua) se
// deja sin definir (= impresa); si falta en una, esa cuenta como impresa.
function mergePrintedUnits(a, b) {
  if (a?.unidadesImpresas == null && b?.unidadesImpresas == null) return undefined;
  const pa = a?.unidadesImpresas == null ? Number(a?.quantity) || 0 : Number(a.unidadesImpresas) || 0;
  const pb = b?.unidadesImpresas == null ? Number(b?.quantity) || 0 : Number(b.unidadesImpresas) || 0;
  return pa + pb;
}

function syncProductIntoCart(state, payload) {
  const action = { payload };
  const product = action.payload;
  const isEmptyCart = state.checkout.cart.length === 0;
  var existed = false;

  // 2026-09-04 · counterDetails = `{[cid]: [{lineIndex, unidades}]}`.
  // Traca las filas físicas Pedido_Producto agrupadas en este DTO
  // — el frontend lo consume para construir entries de
  // MoverProductos con LineIndex real (post-split). Backend expone
  // `lineDetails` en el DTO padre agrupado por {counter, comensal},
  // cada detail incluye su CounterProductID (necesario porque
  // GetCart puede fusionar 2 counters del mismo bucket).
  const initialCounterDetails = {};
  if (Array.isArray(product.lineDetails) && product.lineDetails.length > 0) {
    product.lineDetails.forEach((d) => {
      const cid = Number(d.counterProductID ?? product.idNumPedido);
      if (!initialCounterDetails[cid]) initialCounterDetails[cid] = [];
      initialCounterDetails[cid].push({
        lineIndex: Number(d.lineIndex) || 0,
        unidades: Number(d.unidades) || 0,
      });
    });
  } else if (product.idNumPedido != null) {
    initialCounterDetails[product.idNumPedido] = [
      { lineIndex: 0, unidades: Number(product.quantity) || 0 },
    ];
  }

  // 2026-10-08 · Imprimir/reimprimir líneas: cada pedido/contador que se
  // agrupa en la línea guarda sus unidades e impresas (`unidadesImpresas`
  // de la API; ausente = impresa) para repartir la impresión por pedido.
  const printPart =
    product.idNumPedido != null
      ? {
          idPedido: product.idPedido ?? null,
          counterProductId: product.idNumPedido,
          units: Number(product.quantity) || 0,
          printed: product.unidadesImpresas == null ? null : Number(product.unidadesImpresas),
        }
      : null;

  if (isEmptyCart) {
    state.checkout.cart = [
      ...state.checkout.cart,
      {
        ...product,
        counterProductId: [product.idNumPedido],
        counterDetails: initialCounterDetails,
        printParts: printPart ? [printPart] : [],
      },
    ];
  } else {
    state.checkout.cart = map(state.checkout.cart, (_product) => {
      var isExisted = isTheSameProduct(product, _product);

      if (isExisted) {
        existed = true;
        var subtotal =
          (_product.quantity + product.quantity) * _product.precio;

        return {
          ..._product,
          notas: product.notas,
          quantity: _product.quantity + product.quantity,
          unidadesPagadas:
            _product.unidadesPagadas + product.unidadesPagadas,
          subtotal: subtotal,
          counterProductId: [
            ..._product.counterProductId,
            product.idNumPedido,
          ],
          counterDetails: {
            ...(_product.counterDetails || {}),
            ...initialCounterDetails,
          },
          printParts: [...(_product.printParts || []), ...(printPart ? [printPart] : [])],
          // Los detalles por pedido se acumulan (antes quedaban los del primero).
          lineDetails: [...(_product.lineDetails || []), ...(product.lineDetails || [])],
          unidadesImpresas: mergePrintedUnits(_product, product),
        };
      }

      return _product;
    });
  }

  if (!existed && !isEmptyCart) {
    state.checkout.cart = [
      ...state.checkout.cart,
      {
        ...product,
        counterProductId: [product.idNumPedido],
        counterDetails: initialCounterDetails,
        printParts: printPart ? [printPart] : [],
      },
    ];
  }

  state.checkout.cart = normalizeCartLines(state.checkout.cart);

  const subtotal = sum(
    state.checkout.cart.map((product) => product.precio * product.quantity),
  );
  state.checkout.subtotal = subtotal;
  state.checkout.total = subtotal;
}

const slice = createSlice({
  name: "product",
  initialState,
  extraReducers: (builder) => {
    // Me han quitado la mesa abierta: se cierran el diálogo de complementos
    // y el de notas, y se descarta el producto a medio configurar (aún no
    // estaba en el carrito; lo del carrito ya lo guardó el bridge en el
    // borrador de la mesa).
    builder.addCase(mesaKicked, (state) => {
      state.openComplementos = false;
      state.isNotasOpen = false;
      state.selectedProduct = -1;
      state.selectedSubProduct = -1;
      state.selectedComplemento = null;
      state.selectedIdComplemento = -1;
      state.unidadesSelectedProduct = -1;
      state.selectedCopa = null;
      state.unidadesCopa = -1;
    });
  },
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    changeIsLoadingPayment(state, action) {
      state.isLoadingPayment = action.payload;
    },

    getProductSuccessById(state, action) {
      const id = action.payload;

      if (id) {
        state.product = state.products.find((p) => Number(p.id) === Number(id));

        if (state.product && state.product.images) {
          state.images = state.product.images.map((image) => ({
            name: image,
            data: image,
          }));
        }
      }

      state.complementosValues = state.products
        .filter((p) => p.isAvailable && p.isActive)
        .map((p, i) => {
          let complement_found = state.product?.complementos.find(
            (c) => c.id === p.id,
          );

          if (complement_found === undefined) {
            return {
              i: i, // i is only to print the product
              idProduct: p.id,
              idCategory: p.idCategory,
              name: p.nombre,
              price: 0.0,
              checked: false,
              order: null,
              isMandatory: true,
              maxQuantity: 1,
              courseLabelId: null,
            };
          } else {
            // complement_found may doesnt have these parameters
            return {
              i: i,
              idProduct: complement_found.id,
              idCategory: complement_found.idCategory,
              name: complement_found.nombre,
              price: complement_found.precio,
              checked: true,
              order: complement_found.order,
              isMandatory: complement_found.isMandatory,
              maxQuantity: complement_found.maxQuantity,
              // Etiqueta de curso del GRUPO (Complemento.CourseLabelId).
              // Sin esta línea el AddComplements NO veía la etiqueta
              // guardada y el select "Curso" del grupo se pintaba vacío
              // aunque el backend la tenía persistida.
              // El backend serializa como `complementCourseLabelId` para
              // evitar colisión de nombre JSON con `CourseLabelID` (línea
              // pedido) en SignalR. Fallback a `courseLabelId` por si
              // algún endpoint aún devuelve el nombre viejo.
              courseLabelId:
                complement_found.complementCourseLabelId ??
                complement_found.courseLabelId ??
                null,
            };
          }
        });

      if (state.complementosValues.length > 0) state.haveComplemento = true;
    },

    cleanProduct(state) {
      state.product = null;
      state.complementosValues = [];
    },

    handleChangeLastCategorySelectedWhenCreate(state, action) {
      state.lastCategorySelectedWhenCreate = action.payload;
    },

    setEditComplementProduct(state, action) {
      state.onEditComplementProduct = action.payload;
    },

    onAddPartialpayment(state, action) {
      state.partialPayment = action.payload;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
      state.createdSuccess = false;
    },

    handleNextPage(state, action) {
      state.page = state.page + 1;
    },

    previousPage(state, action) {
      state.page = state.page - 1;
    },

    resetPage(state, action) {
      state.page = 0;
    },

    handleFilterName(state, action) {
      state.filterName = action.payload;
    },

    setProductCategoryFilter(state, action) {
      state.productCategoryFilter = action.payload; // array of category ids
    },

    cleanProducts(state) {
      state.product = null;
      state.products = [];
      state.frequentProducts = [];
      state.lastTimeRequest = null;
    },

    // CLEAN IMAGES SUCCESS
    cleanImagesSuccess(state, action) {
      state.images = [];
    },

    updateCategoryOrderUpdate(state, action) {
      state.categoryCreateOrder = action.payload;
    },

    // GET PRODUCTS
    getProductsSuccess(state, action) {
      state.isLoading = false;
      state.products = action.payload;
      state.lastTimeRequest = new Date();
      state.productoNota = state.products.map((p) => {
        var checkout = state.checkout.cart.find((pro) => pro.id == p.id);
        if (checkout) {
          return {
            id: p.id,
            isOpen: false,
            nota: checkout.notas != "" ? checkout.notas : "",
          };
        } else {
          return {
            id: p.id,
            isOpen: false,
            nota: "",
          };
        }
      });

      // Recomputa complementosValues siempre que lleguen productos:
      // - Edición: cruza con state.product.complementos para marcar los ya seleccionados
      // - Creación (state.product === null): todos sin marcar
      state.complementosValues = state.products
        .filter((p) => p.isAvailable && p.isActive)
        .map((p, i) => {
          const complement_found = state.product?.complementos.find(
            (c) => c.id === p.id,
          );
          if (!complement_found) {
            return {
              i,
              idProduct: p.id,
              idCategory: p.idCategory,
              name: p.nombre,
              price: 0.0,
              checked: false,
              order: null,
              isMandatory: true,
              maxQuantity: 1,
              courseLabelId: null,
            };
          }
          return {
            i,
            idProduct: complement_found.id,
            idCategory: complement_found.idCategory,
            name: complement_found.nombre,
            price: complement_found.precio,
            checked: true,
            order: complement_found.order,
            isMandatory: complement_found.isMandatory,
            maxQuantity: complement_found.maxQuantity,
            // Etiqueta de curso del grupo (persistida en Complemento.CourseLabelId).
            // Backend serializa como `complementCourseLabelId`; fallback a
            // `courseLabelId` para compat retro.
            courseLabelId:
              complement_found.complementCourseLabelId ??
              complement_found.courseLabelId ??
              null,
          };
        });
    },

    changeOrderCategory(state, action) {
      const { oldIndex, newIndex } = action.payload;
      let aux = state.categoryCreateOrder.map((c) => {
        if (c.order === oldIndex) {
          return { ...c, order: newIndex };
        }

        if (c.order === newIndex) {
          return { ...c, order: oldIndex };
        }

        return c;
      });

      // order aux array by order
      aux = aux.sort((a, b) => a.order - b.order);

      state.categoryCreateOrder = aux;
    },

    // Renombrado a *Success para poder EXPORTAR la acción sin chocar con
    // el thunk `getFrequentProducts` de más abajo. Uso: `dispatch(
    // getFrequentProductsSuccess(products))` cuando ya tienes el payload
    // resuelto (p.ej. desde loadTpvBootstrap sin re-fetchear).
    getFrequentProductsSuccess(state, action) {
      state.isLoading = false;
      state.frequentProducts = action.payload;
    },

    // CHANGE PAGE
    changePageSuccess(state, action) {
      state.selectedPage = action.payload;
    },

    changeProductsPerPage(state, action) {
      state.productPerPage = action.payload;
    },

    // GET PRODUCT
    getProductSuccess(state, action) {
      state.isLoading = false;
      state.product = action.payload;
    },

    updateProductSuccess(state, action) {
      state.isLoading = false;
      state.product = action.payload;
      state.updatedSuccess = true;
    },
    createProductSuccess(state, action) {
      state.isLoading = false;
      state.product = action.payload;
      state.products = [...state.products, action.payload];
      state.createdSuccess = true;
    },
    getMenusSuccess(state, action) {
      state.isLoading = false;
      state.products = action.payload;
      state.createdSuccess = false;
    },
    getMenuSuccess(state, action) {
      state.isLoading = false;
      state.menus = action.payload;
      state.createdSuccess = false;
    },
    createMenuSuccess(state, action) {
      state.isLoading = false;
      state.menu = action.payload;
      state.menus = [...state.menus, action.payload];
      state.createdSuccess = true;
    },
    refreshProductSuccess(state, action) {
      state.product = null;
    },
    //  SORT & FILTER PRODUCTS
    sortByProducts(state, action) {
      state.sortBy = action.payload;
    },

    filterProducts(state, action) {
      state.filters.category = action.payload.category;
      state.filters.alergenos = action.payload.alergenos;
      state.filters.priceRange = action.payload.priceRange;
      //state.filters.bebida = action.payload.bebida;
      state.filters.productName = action.payload.productName;
    },

    // CHECKOUT
    getCart(state, action) {
      var cart = action.payload;
      cart = cart.filter((p) => p.quantity > 0);

      const subtotal = sum(
        cart.map((product) => product.precio * product.quantity),
      );
      const discount = cart.length === 0 ? 0 : state.checkout.discount;
      const shipping = cart.length === 0 ? 0 : state.checkout.shipping;
      const billing = cart.length === 0 ? null : state.checkout.billing;

      state.checkout.cart = cart;
      state.checkout.discount = discount;
      state.checkout.shipping = shipping;
      state.checkout.billing = billing;
      state.checkout.subtotal = subtotal;
      state.checkout.total = subtotal - discount;
    },

    setSelectedProductSuccess(state, action) {
      state.selectedProduct = action.payload;
    },

    setSelectedUnidadesSuccess(state, action) {
      state.unidadesSelectedProduct = action.payload;
    },

    addOldCartAndClean(state, action) {
      let newElementsCart = state.checkout.cart.filter((p) => !p.old);

      state.checkout.cart = state.checkout.cart.filter((p) => p.old);

      if (newElementsCart && newElementsCart.length > 0) {
        newElementsCart.forEach((p) => {
          let product = { ...p, old: true };

          if (!product?.unidades) product.unidades = product?.quantity;
          if (!product?.unidadesPagadas) product.unidadesPagadas = 0;

          const isEmptyCart = state.checkout.cart.length === 0;
          var existed = false;

          if (isEmptyCart) {
            state.checkout.cart = [...state.checkout.cart, product];
          } else {
            state.checkout.cart = map(state.checkout.cart, (_product) => {
              var isExisted = isTheSameProduct(product, _product);

              if (isExisted) {
                existed = true;
                var subtotal =
                  (_product.quantity + product.quantity) * _product.precio;
                return {
                  ..._product,
                  notas: product.notas,
                  quantity: _product.quantity + product.quantity,
                  subtotal: subtotal,
                };
              }
              return _product;
            });
          }

          if (!existed && !isEmptyCart) {
            state.checkout.cart = [...state.checkout.cart, product];
          }

          state.checkout.cart = normalizeCartLines(state.checkout.cart);

          const subtotal = sum(
            state.checkout.cart.map(
              (product) => product.precio * product.quantity,
            ),
          );
          state.checkout.subtotal = subtotal;
          state.checkout.total = subtotal;
        });
      }
    },

    addCart(state, action) {
      addProductToCart(state, action.payload);
    },

    // Fase 3A · equivale a N `addCart` seguidos, en un solo dispatch.
    addCartMany(state, action) {
      for (const item of action.payload || []) addProductToCart(state, item);
    },

    addEditCart(state, action) {
      const { product, original } = action.payload;

      state.checkout.cart = map(state.checkout.cart, (_product) => {
        var isExisted = isTheSameProduct(original, _product);

        if (isExisted) {
          var subtotal = product.quantity * product.precio;
          return {
            ...product,
            notas: product.notas,
            quantity: product.quantity,
            subtotal: subtotal,
          };
        }
        return _product;
      });

      const subtotal = sum(
        state.checkout.cart.map((product) => product.precio * product.quantity),
      );
      state.checkout.subtotal = subtotal;
      state.checkout.total = subtotal;
    },

    /**
     * Sustituye N lines originales de un producto compuesto multi-comensal
     * por M lines nuevas en una sola operación atómica. Necesario porque:
     *
     *   - addCart hace merge/append: si el usuario deja 1 comensal donde
     *     había 2, se acumulan (2 originales + 1 nuevo = 3 lines).
     *   - addEditCart reemplaza 1-a-1 por `isTheSameProduct`: si N != M,
     *     sobran/faltan lines y las viejas no se limpian.
     *
     * Reducer:
     *   1. Filtra del cart TODAS las lines que matchean cualquier
     *      `originals[i]` por (id, notas, idComensal) — mismo criterio
     *      que `isTheSameProduct` acepta.
     *   2. Añade `products` al principio del cart en el mismo orden.
     */
    replaceMenuLines(state, action) {
      const { originals, products } = action.payload;
      if (!Array.isArray(originals) || originals.length === 0) return;

      // 1) Filtramos los originales del cart. Comparación por
      //    (id, idComensal) — suficiente porque `_menuLines` viene del
      //    cart y comparte el `id` del producto padre; el idComensal
      //    identifica la line dentro del producto.
      //    Edge case: idComensal puede venir undefined en productos
      //    compuestos simples (1 line, sin multi-comensal). Comparamos
      //    tratando undefined/null como iguales para no perder el match.
      const normalizeIdComensal = (v) => (v == null ? null : Number(v));
      state.checkout.cart = state.checkout.cart.filter((cartLine) => {
        return !originals.some(
          (orig) =>
            orig.id === cartLine.id
            && normalizeIdComensal(orig.idComensal) === normalizeIdComensal(cartLine.idComensal)
            && !cartLine.old
            && !cartLine.servido,
        );
      });

      // 2) Añadimos las nuevas. Cada `product` ya viene con quantity,
      //    unidades, subtotal precomputados por el CartComplement.
      const normalized = products.map((p) => {
        const q = p.quantity ?? p.unidades ?? 1;
        return {
          ...p,
          quantity: q,
          unidades: 0,
          unidadesPagadas: 0,
          subtotal: q * (p.precio || 0),
        };
      });
      state.checkout.cart = [...normalized, ...state.checkout.cart];

      const subtotal = sum(
        state.checkout.cart.map((product) => product.precio * product.quantity),
      );
      state.checkout.subtotal = subtotal;
      state.checkout.total = subtotal;
    },

    // ────────────────────────────────────────────────────────────────
    // 2026-09-03 · Split-by-comensal (Hiopos-like)
    // ────────────────────────────────────────────────────────────────

    /**
     * Cambia el tab de comensal activo. `null` = ningún tab activo (el
     * bucket "Todos" está seleccionado; nuevos productos van a C0).
     */
    setActiveComensal(state, action) {
      state.dinerSplit = state.dinerSplit || { activeComensal: null, comensales: [] };
      state.dinerSplit.activeComensal = action.payload;
    },

    /**
     * Cachea la lista de comensales de la sesión actual (viene del
     * backend tras cualquier CRUD /api/dinerSplit).
     */
    setComensales(state, action) {
      state.dinerSplit = state.dinerSplit || { activeComensal: null, comensales: [] };
      state.dinerSplit.comensales = Array.isArray(action.payload) ? action.payload : [];
    },

    /**
     * Mueve N líneas del cart (locales O comandadas) a otro comensal.
     * Payload: { productoIds: [{id, idComplemento, notas, precio,
     *   idComensal}], newComensal }.
     *
     * 2026-09-03 · Ya NO filtra por `line.old` — el thunk
     * `moverProductosComensalBackend` persiste primero en backend y
     * luego dispatcha este reducer con TODAS las líneas afectadas
     * (comandadas y locales). Sin este cambio, las líneas ya
     * comandadas no reflejaban el nuevo comensal en el chip.
     */
    moveProductsToComensal(state, action) {
      const { productIds, newComensal, units } = action.payload || {};
      if (!Array.isArray(productIds) || productIds.length === 0) return;
      const normalized = productIds.map((p) => ({
        id: p.id,
        idComplemento: p.idComplemento,
        notas: p.notas,
        precio: p.precio,
        idComensalOld: p.idComensal ?? 0,
        // Promociones horarias: si el caller la informa, distingue
        // unidades con/sin promo del mismo producto y precio.
        priceRuleId: p.priceRuleId,
      }));
      const sameRule = (p, line) =>
        p.priceRuleId === undefined
        || String(p.priceRuleId ?? "") === String(line.priceRuleId ?? "");
      const targetComensal = Number(newComensal) || 0;
      // Si viene `units` (split parcial), aplicamos FIFO: consumimos
      // hasta `units` unidades de las líneas matching. Si una línea
      // tiene MÁS de las que necesitamos, la partimos en dos:
      // (units movidas al nuevo comensal) + (resto en el original).
      // Sin `units` = mover TODAS las unidades matching (comportamiento
      // legacy).
      if (units == null || units <= 0) {
        state.checkout.cart = state.checkout.cart.map((line) => {
          const match = normalized.find(
            (p) =>
              p.id === line.id
              && p.idComplemento === line.idComplemento
              && p.notas === line.notas
              && Number(p.precio) === Number(line.precio)
              && (p.idComensalOld ?? 0) === (line.idComensal ?? 0)
              && sameRule(p, line),
          );
          if (!match) return line;
          return { ...line, idComensal: targetComensal };
        });
        return;
      }
      let remaining = Number(units) || 0;
      const next = [];
      for (const line of state.checkout.cart) {
        if (remaining <= 0) { next.push(line); continue; }
        const match = normalized.find(
          (p) =>
            p.id === line.id
            && p.idComplemento === line.idComplemento
            && p.notas === line.notas
            && Number(p.precio) === Number(line.precio)
            && (p.idComensalOld ?? 0) === (line.idComensal ?? 0)
            && sameRule(p, line),
        );
        if (!match) { next.push(line); continue; }
        const q = Number(line.quantity) || 0;
        if (q <= remaining) {
          next.push({ ...line, idComensal: targetComensal });
          remaining -= q;
        } else {
          // Split — la parte movida al nuevo comensal + la parte que
          // queda en el original.
          next.push({ ...line, quantity: remaining, idComensal: targetComensal });
          next.push({ ...line, quantity: q - remaining });
          remaining = 0;
        }
      }
      // 2026-09-07 · Fusión post-split. Regla del negocio: mismo
      // producto+comensal = 1 sola línea (nunca 2 con quantities
      // separadas). Si algún split acaba con 2 líneas idénticas por
      // (id+idComplemento+notas+precio+courseLabelID+idComensal) las
      // consolidamos sumando quantities. Sin esto, un reparto que
      // deje unidades en el bucket original + otro grupo movido al
      // mismo bucket pintaba líneas duplicadas que compartían estado.
      const merged = [];
      for (const line of next) {
        const idx = merged.findIndex((m) => isTheSameProduct(m, line));
        if (idx === -1) {
          merged.push({ ...line });
          continue;
        }
        merged[idx] = {
          ...merged[idx],
          quantity: (Number(merged[idx].quantity) || 0) + (Number(line.quantity) || 0),
          subtotal:
            ((Number(merged[idx].quantity) || 0) + (Number(line.quantity) || 0))
            * (Number(merged[idx].precio) || 0),
        };
      }
      state.checkout.cart = merged;
    },

    /**
     * Sustituye el diccionario `subtotalPorComensal` del checkout tras
     * un mover-productos. El backend lo recalcula y devuelve en la
     * misma respuesta — el front lo aplica sin refetch adicional.
     */
    setSubtotalPorComensal(state, action) {
      state.checkout.subtotalPorComensal = action.payload || {};
    },

    /**
     * 2026-09-07 · Mutación optimista del flag isPartial en el
     * checkout.cart tras marcarMenuParcial. Payload:
     * `{ idPedido, counterProductId, isPartial }`. Sin esto el chip
     * "incompleto" del cart local no se actualiza hasta el próximo
     * SyncCart (SignalR o refresh manual).
     */
    setCartLinePartialFlag(state, action) {
      const { idPedido, counterProductId, isPartial } = action.payload || {};
      if (idPedido == null || counterProductId == null) return;
      const flag = !!isPartial;
      state.checkout.cart = (state.checkout.cart || []).map((line) => {
        if (Number(line.idPedido) !== Number(idPedido)) return line;
        // El cart puede tener `counterProductId` array (post-fraccionado)
        // o `idNumPedido` (single). Cubrimos ambos.
        const arr = Array.isArray(line.counterProductId)
          ? line.counterProductId
          : line.idNumPedido != null
            ? [line.idNumPedido]
            : [];
        if (!arr.map(Number).includes(Number(counterProductId))) return line;
        return { ...line, isPartial: flag };
      });
    },

    /**
     * 2026-09-04 · Reasigna todas las líneas del cart de un comensal
     * borrado al bucket "sin asignar" (C0). Se llama tras
     * `deleteComensal` para reflejar al instante el reset del comensal
     * eliminado — el backend ya hizo el ExecuteUpdateAsync equivalente,
     * pero sin este reducer el cart local mantenía el idComensal viejo
     * hasta el próximo refetch/SyncCart.
     * Payload: `{ numero }`.
     */
    reassignComensalToUnassigned(state, action) {
      const numero = Number(action.payload?.numero) || 0;
      if (numero <= 0) return;
      state.checkout.cart = (state.checkout.cart || []).map((line) => {
        if ((Number(line.idComensal) || 0) === numero) {
          return { ...line, idComensal: 0 };
        }
        return line;
      });
      // También el subtotal por comensal (si existe) — trasvase al C0.
      if (state.checkout.subtotalPorComensal) {
        const map = { ...state.checkout.subtotalPorComensal };
        if (map[numero]) {
          map[0] = (map[0] || 0) + map[numero];
          delete map[numero];
          state.checkout.subtotalPorComensal = map;
        }
      }
    },

    /**
     * 2026-09-03 · FraccionarDialog. Aplica el resultado del kanban
     * (workingLines con idComensal mutado) SUSTITUYENDO íntegramente
     * las líneas del cart que estaban en el snapshot inicial. Preserva
     * el orden natural del cart. Match por `lineKey` estable (pedidoID
     * + productoID + complementoID + counterProductID + lineIndex +
     * notas + precio).
     *
     * `moverProductosComensalBackend` con `moveProductsToComensal` no
     * sirve porque matchea por producto+precio → colisiona con líneas
     * repetidas del mismo producto en distintos comensales. Aquí hacemos
     * match exacto por lineKey.
     *
     * Payload: `{ updates: [{ lineKey, idComensal }], newLines: [...] }`
     * — `updates` para actualizar existing, `newLines` para reemplazar
     * el conjunto entero cuando el user splitteó líneas (más de 1 line
     * con el mismo lineKey y quantities distintas).
     */
    /**
     * 2026-09-03 · Muta el `checkout.cart` aplicando idComensal por
     * counterProductID exacto. Usado por `moverProductosComensalMulti`
     * como mutación optimista para que el sidebar del checkout refleje
     * el cambio inmediatamente sin esperar refetch.
     *
     * Payload: `{ assignments: [{ counterProductID, complementoID, newComensal }] }`
     *
     * Comportamiento: recorre cart. Cuando una línea contiene el
     * counterProductID en su array, la splittea:
     * - Si TODAS las unidades del array van al mismo newComensal → una
     *   sola línea con nuevo idComensal.
     * - Si solo ALGUNAS → 1 sub-línea por (nuevo comensal + counter subset)
     *   + 1 línea residual con los counters no movidos.
     * Sin colisión con productos iguales de distintos comensales
     * (matchea por PK, no por producto+precio).
     */
    applyComensalDiffByCounter(state, action) {
      const { assignments } = action.payload || {};
      if (!Array.isArray(assignments) || assignments.length === 0) return;
      // Mapa counterProductID → newComensal (respetando complementoID).
      const map = new Map();
      assignments.forEach((a) => {
        const key = `${a.counterProductID}::${a.complementoID ?? -1}`;
        map.set(key, Number(a.newComensal) || 0);
      });
      const nextCart = [];
      for (const line of state.checkout.cart) {
        const ids = Array.isArray(line.counterProductId)
          ? line.counterProductId
          : line.idNumPedido != null ? [line.idNumPedido] : [];
        const complementKey = line.idComplemento ?? -1;
        // Reparto: por cada counterProductID de la línea, resolver
        // nuevo comensal (asignado → nuevo, no asignado → idComensal
        // actual). Agrupar por comensal resultante.
        const byComensal = new Map();
        ids.forEach((cid) => {
          const k = `${cid}::${complementKey}`;
          const newC = map.has(k) ? map.get(k) : (Number(line.idComensal) || 0);
          const arr = byComensal.get(newC) || [];
          arr.push(cid);
          byComensal.set(newC, arr);
        });
        if (byComensal.size === 0) {
          nextCart.push(line);
          continue;
        }
        if (byComensal.size === 1) {
          // Todos los counters van al mismo comensal → 1 sola línea.
          const [[newC, cids]] = Array.from(byComensal.entries());
          nextCart.push({
            ...line,
            idComensal: newC,
            counterProductId: cids,
            idNumPedido: cids[0] ?? line.idNumPedido,
          });
          continue;
        }
        // Split — 1 sub-línea por comensal con su subset de counters.
        Array.from(byComensal.entries()).forEach(([newC, cids]) => {
          nextCart.push({
            ...line,
            quantity: cids.length,
            idComensal: newC,
            counterProductId: cids,
            idNumPedido: cids[0] ?? line.idNumPedido,
          });
        });
      }
      // 2026-09-04 · Fusión de líneas idénticas post-split. Si el
      // mover-productos deja 2 sub-líneas con exactamente el mismo
      // producto+precio+complementos+notas+idComensal, colapsarlas en
      // una sola con quantity sumada y counterProductId concatenado —
      // consistencia con backend GetCart (que también agrupa así). Sin
      // esto el cart local pintaba 2 filas y backend 1 → discrepancia
      // visual al recargar.
      const merged = [];
      for (const line of nextCart) {
        const idx = merged.findIndex((m) => isTheSameProduct(m, line));
        if (idx === -1) { merged.push(line); continue; }
        const ex = merged[idx];
        const cids = [
          ...(Array.isArray(ex.counterProductId) ? ex.counterProductId : []),
          ...(Array.isArray(line.counterProductId) ? line.counterProductId : []),
        ];
        merged[idx] = {
          ...ex,
          quantity: (Number(ex.quantity) || 0) + (Number(line.quantity) || 0),
          counterProductId: cids,
          idNumPedido: ex.idNumPedido ?? line.idNumPedido,
        };
      }
      state.checkout.cart = merged;
    },

    applyFraccionarResult(state, action) {
      const { updates, newLines } = action.payload || {};
      if (Array.isArray(newLines) && newLines.length > 0) {
        // 2026-09-04 · Fusión post-hoc de líneas idénticas — si tras
        // el fraccionado quedan 2 sub-líneas con mismo producto +
        // precio + complementos + notas + idComensal + old, deben
        // consolidarse en 1 línea con quantity sumada y counterProductId
        // concatenado. Sin este merge el cart pinta 2 filas separadas
        // del "mismo" producto mientras backend (GetCart con idComensal
        // en el predicate) devuelve 1 sola línea con quantity 2 →
        // discrepancia visual entre front y backend al recargar.
        const merged = [];
        for (const line of newLines) {
          const existingIdx = merged.findIndex((m) => isTheSameProduct(m, line));
          if (existingIdx === -1) {
            merged.push({ ...line });
            continue;
          }
          const existing = merged[existingIdx];
          const cids = [
            ...(Array.isArray(existing.counterProductId) ? existing.counterProductId : []),
            ...(Array.isArray(line.counterProductId) ? line.counterProductId : []),
          ];
          merged[existingIdx] = {
            ...existing,
            quantity: (Number(existing.quantity) || 0) + (Number(line.quantity) || 0),
            counterProductId: cids,
            idNumPedido: existing.idNumPedido ?? line.idNumPedido,
            subtotal:
              ((Number(existing.quantity) || 0) + (Number(line.quantity) || 0))
              * (Number(existing.precio) || 0),
          };
        }
        state.checkout.cart = merged;
        return;
      }
      if (!Array.isArray(updates) || updates.length === 0) return;
      const map = new Map(updates.map((u) => [u.lineKey, Number(u.idComensal) || 0]));
      const keyOf = (l) =>
        `${l.idPedido ?? "cart"}::${l.id}::${l.idComplemento ?? -1}::${l.idNumPedido ?? "new"}::${l.lineIndex ?? 0}::${l.notas || ""}::${Number(l.precio) || 0}`;
      state.checkout.cart = state.checkout.cart.map((line) => {
        const k = keyOf(line);
        if (!map.has(k)) return line;
        return { ...line, idComensal: map.get(k) };
      });
    },

    saveHistoryCheckout(state, action) {
      let products = state.checkout.cart;
      let idMesa = action.payload;

      if (products.some((p) => !p.old && !p.servido)) {
        if (state.checkoutHistory.length === 0) {
          state.checkoutHistory = [
            {
              idMesa: idMesa,
              savedAt: Date.now(),
              checkout: {
                ...state.checkout,
                cart: state.checkout.cart.filter((u) => !u.old && !u.servido),
              },
            },
          ];
        } else {
          let exist = state.checkoutHistory.find((u) => u.idMesa === idMesa);
          if (exist) {
            state.checkoutHistory = state.checkoutHistory.map((m) => {
              if (m.idMesa === idMesa) {
                return {
                  idMesa: idMesa,
                  savedAt: Date.now(),
                  checkout: {
                    ...state.checkout,
                    cart: state.checkout.cart.filter(
                      (u) => !u.old && !u.servido,
                    ),
                  },
                };
              }
              return m;
            });
          } else {
            state.checkoutHistory = [
              ...state.checkoutHistory,
              {
                idMesa: idMesa,
                savedAt: Date.now(),
                checkout: {
                  ...state.checkout,
                  cart: state.checkout.cart.filter((u) => !u.old && !u.servido),
                },
              },
            ];
          }
        }
      } else {
        state.checkoutHistory = state.checkoutHistory.filter(
          (m) => m.idMesa !== idMesa,
        );
      }
    },

    syncCartCheckoutComplete(state, action) {
      // Las líneas sin enviar (old=false) son solo locales: se conservan
      // siempre delante de las que trae el servidor. Sin mutar el payload
      // (antes se escribía su `cart`, y reaplicar el mismo objeto duplicaba).
      const newElementsCart = state.checkout.cart.filter((u) => !u.old);
      const payload = action.payload || {};
      state.checkout = { ...payload, cart: [...newElementsCart, ...(payload.cart || [])] };
      // Los totales del servidor solo cuentan lo enviado: se suman las líneas
      // locales sin enviar (antes el carrito restaurado marcaba 0 € con
      // líneas del borrador hasta volver a entrar en la carta).
      const localExtra = sum(newElementsCart.map((p) => (Number(p.precio) || 0) * (Number(p.quantity) || 0)));
      if (localExtra) {
        state.checkout.subtotal = (Number(payload.subtotal) || 0) + localExtra;
        state.checkout.total = (Number(payload.total) || 0) + localExtra;
      }
    },

    syncCartCheckout(state, action) {
      syncProductIntoCart(state, action.payload);
    },

    // Fase 3A · equivale a N `syncCartCheckout` seguidos, en un solo dispatch.
    syncCartCheckoutMany(state, action) {
      for (const item of action.payload || []) syncProductIntoCart(state, item);
    },

    // Descarta borradores de mesa de más de `maxAgeMs` (por defecto 24 h)
    // para que nunca reaparezcan líneas antiguas tras días sin usar el TPV.
    pruneCheckoutHistory(state, action) {
      const maxAgeMs = action?.payload?.maxAgeMs ?? 24 * 60 * 60 * 1000;
      const now = action?.payload?.now ?? Date.now();
      state.checkoutHistory = (state.checkoutHistory || []).filter(
        (m) => m && m.savedAt && now - m.savedAt <= maxAgeMs,
      );
    },

    // 2026-10-08 · Auto-guardado: deja en el borrador de `idMesa` solo
    // `cart` (lo que NO se ha guardado, p. ej. menús incompletos). Vacío =
    // sin borrador.
    setHistoryDraftCart(state, action) {
      const { idMesa, cart } = action.payload || {};
      if (idMesa == null) return;
      const lines = (cart || []).filter((u) => !u.old && !u.servido);
      const rest = (state.checkoutHistory || []).filter((m) => m.idMesa !== idMesa);
      if (!lines.length) {
        state.checkoutHistory = rest;
        return;
      }
      const prev = (state.checkoutHistory || []).find((m) => m.idMesa === idMesa);
      state.checkoutHistory = [
        ...rest,
        {
          idMesa,
          savedAt: Date.now(),
          checkout: { ...(prev?.checkout || {}), cart: lines },
        },
      ];
    },

    cleanHistoryMesa(state, action) {
      state.checkoutHistory = state.checkoutHistory.filter(
        (m) => m.idMesa !== action.payload,
      );
    },

    cleanOldCart(state, action) {
      state.checkout.cart = state.checkout.cart.filter((p) => p.old);
    },

    addOldCart(state, action) {
      const product = action.payload;
      const isEmptyCart = state.checkout.oldCart.length === 0;
      var existed = false;

      if (isEmptyCart) {
        state.checkout.oldCart = [...state.checkout.oldCart, product];
      } else {
        state.checkout.oldCart = map(state.checkout.oldCart, (_product) => {
          var isExisted =
            _product.id === product.id &&
            _product.idComplemento === product.idComplemento &&
            _product.notas === product.notas;

          if (isExisted && _product.complementos.length > 0) {
            isExisted = isTheSameProductWithComplements(product, _product);
          }

          if (isExisted) {
            existed = true;
            var subtotal =
              (_product.quantity + product.quantity) * _product.precio;
            return {
              ..._product,
              notas: product.notas,
              quantity: _product.quantity + product.quantity,
              subtotal: subtotal,
              old: true,
            };
          }
          return _product;
        });
      }

      if (!existed && !isEmptyCart) {
        state.checkout.cart = [...state.checkout.cart, product];
      }

      state.checkout.cart = state.checkout.cart.filter((p) => p.quantity > 0);

      const subtotal = sum(
        state.checkout.cart.map((product) => product.precio * product.quantity),
      );
      state.checkout.subtotal = subtotal;
      state.checkout.total = subtotal;
    },

    updatePaymentProducts(state, action) {
      let struct = action.payload; // [id] { all, products: [{id, unidades}]}
      let newStruct = [];

      struct.products.map((s) => {
        let alreadyExist = newStruct.find((u) => u.idProducto === s.idProducto);

        if (!alreadyExist) {
          newStruct.push({
            idProducto: s.idProducto,
            unidades: s.unidades,
          });
        } else {
          newStruct = newStruct.map((u) => {
            if (u.idProducto === s.idProducto) {
              return {
                ...u,
                unidades: u.unidades + s.unidades,
              };
            }
            return u;
          });
        }
      });

      newStruct = newStruct.filter((u) => u.unidades > 0);

      state.checkout = {
        ...state.checkout,
        cart: state.checkout.cart.map((p) => {
          if (struct.all) return { ...p, unidadesPagadas: p.quantity };
          else {
            let payProduct = newStruct.find(
              (_p) => _p.idProducto === p.id && _p.unidades > 0,
            );

            if (payProduct) {
              if (payProduct.unidades > p.quantity) {
                newStruct = newStruct.map((f) => {
                  if (f.idProducto === payProduct.idProducto) {
                    return {
                      ...f,
                      unidades: f.unidades - p.quantity,
                    };
                  }
                  return f;
                });
              } else {
                newStruct = newStruct.filter(
                  (f) => f.idProducto !== payProduct.idProducto,
                );
              }

              return {
                ...p,
                unidadesPagadas:
                  payProduct.unidades > p.quantity
                    ? p.quantity
                    : payProduct.unidades,
              };
            }
          }
          return p;
        }),
      };

      state.checkout.cart = state.checkout.cart.map((p) => {
        let pendingUnidades = p.quantity - p.unidadesPagadas;

        if (pendingUnidades < 0) pendingUnidades = 0;

        return {
          ...p,
          quantity: pendingUnidades,
          unidades: 0,
          unidadesPagadas: 0,
        };
      });

      const subtotal = sum(
        state.checkout.cart.map((product) => product.precio * product.quantity),
      );

      state.checkout.subtotal = subtotal;
      state.checkout.total = subtotal;
    },

    addInfoCart(state, action) {
      state.checkout.idOrder = action.payload.id;
      state.checkout.isPay = action.payload.isPay;
      state.checkout.buttonPayClicked = action.payload.buttonPayClicked;
    },

    deleteCart(state, action) {
      var productoId = action.payload.productoId;
      var complementoId = action.payload.productoIdComplemento;

      state.checkout.cart = state.checkout.cart.filter(
        (item) =>
          item.id !== productoId || item.idComplemento !== complementoId,
      );
    },
    setComplementosOpen(state, action) {
      state.openComplementos = action.payload;
    },
    // 2026-09-29 · Rollback del reset optimista del carrito al cobrar.
    // Solo restaura si el carrito sigue vacío (el camarero no ha empezado
    // otra mesa) para no pisar trabajo nuevo.
    restoreCheckoutSnapshot(state, action) {
      const { checkout, historyEntry } = action.payload || {};
      if (checkout && (state.checkout.cart || []).length === 0) {
        state.checkout = { ...state.checkout, ...checkout };
      }
      if (
        historyEntry &&
        !state.checkoutHistory.some((h) => h.idMesa === historyEntry.idMesa)
      ) {
        state.checkoutHistory = [...state.checkoutHistory, historyEntry];
      }
    },
    resetCart(state) {
      state.checkout.activeStep = 0;
      state.checkout.cart = [];
      state.checkout.oldCart = [];
      state.checkout.total = 0;
      state.checkout.subtotal = 0;
      state.checkout.discount = 0;
      state.checkout.shipping = 0;
      state.checkout.billing = null;
      state.checkout.idOrder = null;
    },

    /**
     * 2026-09-04 · Reset SOLO de los items OLD (comandados) del cart —
     * preserva intactos los NEW que el camarero está construyendo.
     * Usado por `SyncCart` para rehidratar solo la parte del backend
     * sin destruir el trabajo en curso ni re-mergear los NEW (que
     * generaba filas "fantasma" cuando el activeComensal había
     * cambiado entre snapshot y re-inyección).
     */
    resetOldCartOnly(state) {
      state.checkout.cart = (state.checkout.cart || []).filter((p) => !p.old);
      state.checkout.oldCart = [];
    },

    resetCartOrder(state) {
      state.checkout.activeStep = 0;
      state.checkout.cart = state.checkout.cart.filter((p) => p.old);
      state.checkout.oldCart = [];

      state.checkout.discount = 0;
      state.checkout.shipping = 0;
      state.checkout.billing = null;

      const subtotal = sum(
        state.checkout.cart.map((product) => product.precio * product.quantity),
      );
      state.checkout.subtotal = subtotal;
      state.checkout.total = subtotal;
    },

    onBackStep(state) {
      state.checkout.activeStep -= 1;
    },

    onNextStep(state) {
      state.checkout.activeStep += 1;
    },

    onGotoStep(state, action) {
      const goToStep = action.payload;
      state.checkout.activeStep = goToStep;
    },

    // 2026-09-27 · increase/decrease normalizan idComplemento con
    // `?? -1`. Antes: el bundle padre entraba al cart con
    // idComplemento=undefined pero el caller pasaba `line.idComplemento
    // ?? -1` en el payload → `undefined === -1` = false → matcher
    // fallaba → push NUEVA línea en cada +. Ahora normalización en
    // ambos lados. isTheSameProduct también compara idComplemento
    // normalizado.
    increaseQuantity(state, action) {
      const productId = action.payload.productoId;
      const productoIdComplemento = action.payload.productoIdComplemento ?? -1;
      const p = action.payload.product;

      if (state.checkout.cart.length === 0) {
        state.checkout.cart = [{ ...p, quantity: 1 }];
      } else {
        const idx = state.checkout.cart.findIndex((existing) =>
          existing.id === productId &&
          (existing.idComplemento ?? -1) === productoIdComplemento &&
          isTheSameProduct(existing, p),
        );
        if (idx >= 0) {
          state.checkout.cart = state.checkout.cart.map((item, i) =>
            i === idx ? { ...item, quantity: item.quantity + 1 } : item,
          );
        } else {
          state.checkout.cart = [...state.checkout.cart, { ...p, quantity: 1 }];
        }
      }

      const subtotal = sum(
        state.checkout.cart.map((product) => product.precio * product.quantity),
      );
      state.checkout.subtotal = subtotal;
      state.checkout.total = subtotal;
    },

    decreaseQuantity(state, action) {
      const productId = action.payload.productoId;
      const productoIdComplemento = action.payload.productoIdComplemento ?? -1;
      const p = action.payload.product;

      const idx = state.checkout.cart.findIndex((existing) =>
        existing.id === productId &&
        (existing.idComplemento ?? -1) === productoIdComplemento &&
        isTheSameProduct(existing, p),
      );
      if (idx < 0) return;

      const target = state.checkout.cart[idx];
      const nextQty = target.quantity - 1;
      if (nextQty <= 0) {
        state.checkout.cart = state.checkout.cart.filter((_, i) => i !== idx);
      } else {
        state.checkout.cart = state.checkout.cart.map((item, i) =>
          i === idx ? { ...item, quantity: nextQty } : item,
        );
      }

      const subtotal = sum(
        state.checkout.cart.map((product) => product.precio * product.quantity),
      );
      state.checkout.subtotal = subtotal;
      state.checkout.total = subtotal;
    },

    decreaseQuantityDelete(state, action) {
      const productId = action.payload.productoId;
      const productoIdComplemento = action.payload.productoIdComplemento;
      const p = action.payload.product;

      const updateCart = map(state.checkoutToDelete, (product) => {
        if (
          product.id === productId &&
          product.idComplemento === productoIdComplemento &&
          product.notas === p.notas &&
          product.old === p.old &&
          product.servido === p.servido &&
          product.precio === p.precio &&
          product.courseLabelID === p.courseLabelID &&
          (product.idComensal ?? 0) === (p.idComensal ?? 0)
        ) {
          if (isTheSameProductWithComplements(product, p))
            return {
              ...product,
              quantity: product.quantity - 1,
            };
        }
        return product;
      }).filter((p) => p.quantity - p.unidadesPagadas > 0);

      state.checkoutToDelete = updateCart;

      if (updateCart.length === 0) {
        state.deleteMode = false;
      }
    },

    comandarOrder(state, action) {
      state.checkout = {
        ...state.checkout,
        idOrder: null,
        cart: state.checkout.cart.map((p) => {
          if (p.old)
            return {
              ...p,
              old: true,
            };
          else {
            return {
              ...p,
              old: true,
              servido: action.payload,
            };
          }
        }),
      };
    },

    // ── Optimistic UI para comandar ──────────────────────────────────────
    // Marca todos los items no-old como old con flag pendingSync + un
    // optimisticKey para poder hacer rollback si el server rechaza.
    optimisticComandarStart(state, action) {
      const { optimisticKey, servido, skipPartial = false } = action.payload;
      // `skipPartial` (auto-guardado al salir de la mesa): los menús
      // incompletos («Continuar más tarde» sin pulsar) siguen sin enviar.
      state.checkout.cart = state.checkout.cart.map((p) =>
        p.old || (skipPartial && (p._isPartial || p.isPartial))
          ? p
          : { ...p, old: true, servido, pendingSync: true, optimisticKey },
      );
    },

    // El server confirmó — limpiamos la marca pendiente. Los items pueden quedar
    // en el cart hasta que llegue el push de SignalR con el checkout real; cuando
    // llega, el reconciliado normal los sobrescribe.
    optimisticComandarCommit(state, action) {
      const { optimisticKey } = action.payload;
      state.checkout.cart = state.checkout.cart.map((p) =>
        p.optimisticKey === optimisticKey ? { ...p, pendingSync: false } : p,
      );
    },

    // El server falló — devolvemos los items a no-old para que el camarero los
    // vea como "no comandados" y pueda reintentar.
    optimisticComandarRollback(state, action) {
      const { optimisticKey } = action.payload;
      state.checkout.cart = state.checkout.cart.map((p) =>
        p.optimisticKey === optimisticKey
          ? { ...p, old: false, pendingSync: false, optimisticKey: undefined }
          : p,
      );
    },

    createBilling(state, action) {
      state.checkout.billing = action.payload;
    },

    applyDiscount(state, action) {
      const discount = action.payload;
      state.checkout.discount = discount;
      state.checkout.total = state.checkout.subtotal - discount;
    },

    // Promociones (fidelización) · aplica la recompensa a UNA unidad de una
    // línea del carrito aún no enviada (cobro rápido). Si la línea tiene
    // varias unidades se separa una a una línea nueva marcada con
    // `loyaltyProgramId` y su precio de recompensa; el backend la valida
    // y descuenta los sellos al cobrar. `loyaltyOriginalPrice` permite
    // deshacer y mostrar "antes X €".
    applyLoyaltyRewardToCartLine(state, action) {
      const { index, programId, programName, rewardPrice } = action.payload || {};
      const cart = state.checkout.cart || [];
      const line = cart[index];
      if (!line || line.old || line.loyaltyProgramId) return;
      const qty = Number(line.quantity) || 0;
      if (qty < 1) return;
      const rewarded = {
        ...line,
        quantity: 1,
        loyaltyProgramId: programId,
        loyaltyProgramName: programName || null,
        loyaltyOriginalPrice: line.precio,
        precio: Math.max(0, Number(rewardPrice) || 0),
      };
      if (qty > 1) {
        cart[index] = { ...line, quantity: qty - 1 };
        cart.push(rewarded);
      } else {
        cart[index] = rewarded;
      }
      state.checkout.cart = cart;
      const subtotal = sum(cart.map((p) => p.precio * p.quantity));
      state.checkout.subtotal = subtotal;
      state.checkout.total = subtotal;
    },

    // Deshace la recompensa de una línea del carrito: recupera el precio.
    removeLoyaltyRewardFromCartLine(state, action) {
      const index = action.payload?.index;
      const cart = state.checkout.cart || [];
      const line = cart[index];
      if (!line || !line.loyaltyProgramId) return;
      const {
        loyaltyProgramId, loyaltyProgramName, loyaltyOriginalPrice, ...rest
      } = line;
      cart[index] = { ...rest, precio: loyaltyOriginalPrice ?? line.precio };
      state.checkout.cart = cart;
      const subtotal = sum(cart.map((p) => p.precio * p.quantity));
      state.checkout.subtotal = subtotal;
      state.checkout.total = subtotal;
    },

    applyShipping(state, action) {
      const shipping = action.payload;
      state.checkout.shipping = shipping;
      state.checkout.total =
        state.checkout.subtotal - state.checkout.discount + shipping;
    },
    cleanCheckoutSuccess(state, action) {
      state.checkout = {
        ...state.checkout,
        idOrder: null,
      };
    },
    getCopasSuccess(state, action) {
      state.copas = action.payload;
    },
    copaSelectedSuccess(state, action) {
      state.selectedCopa = action.payload;
    },
    setUniadesCopaSelected(state, action) {
      state.unidadesCopa = action.payload;
    },
    cleanCopaSuccess(state, action) {
      state.selectedCopa = null;
      state.unidadesCopa = -1;
    },
    cleanSelected(state, action) {
      state.selectedComplemento = null;
      state.selectedIdComplemento = -1;
      state.selectedProduct = -1;
      state.unidadesSelectedProduct = -1;
      state.selectedCopa = null;
      state.unidadesCopa = -1;
    },
    selectedComplementoSuccess(state, action) {
      var complemento = action.payload;
      if (complemento != null) {
        var id = complemento.isComplemento
          ? complemento.id + complemento.idComplemento
          : complemento.id;
      }

      state.selectedComplemento = action.payload;
      state.selectedIdComplemento = complemento ? id : -1;
    },
    setOpenNotas(state, action) {
      state.productoNota = state.productoNota.map((p) => {
        if (p.id === action.payload.id) {
          return action.payload;
        } else return p;
      });
    },
    deleteProductSuccess(state, action) {
      state.products = state.products.map((p) => {
        if (p.id === action.payload) {
          return { ...p, isActive: false, deletionDate: new Date() };
        } else return p;
      });
    },
    activateProductSuccess(state, action) {
      state.products = state.products.map((p) => {
        if (p.id === action.payload) {
          return { ...p, isActive: true, deletionDate: null };
        } else return p;
      });
    },
    addImagesSuccess(state, action) {
      state.images = [...state.images, action.payload];
    },
    resetImages(state, action) {
      state.images = [];
    },
    removeSpecificImage(state, action) {
      state.images = state.images.filter((s) => s.name !== action.payload);
    },
    addProductoSuccess(state, action) {
      state.products = [...state.products, action.payload];
    },
    addProductoSimpleSuccess(state, action) {
      // Solo refrescamos la lista de productos. NO tocamos `complementosValues`
      // — el form de edición de producto monta Formik con `enableReinitialize`
      // y `initialValues.complementos = complementosValues`. Si aquí
      // añadiéramos al array, Formik se reinicializaría entero y perdería
      // todos los `checked: true`/cambios en grupos que el usuario hubiera
      // hecho en sesión (incluidos grupos de complementos recién creados).
      // El callback `onSelect` del modal de creación añade el complemento
      // directamente a `values.complementos` vía `setFieldValue`, así que
      // duplicarlo aquí es además redundante.
      state.products = [...state.products, action.payload];
    },
    updateProductoSuccess(state, action) {
      state.products = state.products.map((p) => {
        if (p.id === action.payload.id) return action.payload;
        else return p;
      });

      state.isLoading = false;
      state.updatedSuccess = true;

      if (state.product !== null && state.product.id === action.payload.id) {
        state.product = action.payload;
      }
    },
    updateProductAsAdmin(state, action) {
      state.checkout.cart = state.checkout.cart.map((p, index) => {
        if (
          p.id === action.payload.id &&
          !p.old &&
          index === action.payload.index
        ) {
          return {
            ...p,
            precio: Number(action.payload.precio) / p.quantity,
            notas: action.payload.notas,
            subtotal: Number(action.payload.precio),
          };
        } else {
          return p;
        }
      });


      // reorganize the cart to group the same products by id,
      // let newCart = [];
      // state.checkout.cart.forEach((p) => {
      //   var index = newCart.findIndex(
      //     (x) =>
      //       x.id === p.id &&
      //       x.idComplemento === p.idComplemento &&
      //       x.notas === p.notas &&
      //       x.old === p.old &&
      //       x.servido === p.servido &&
      //       x.precio === p.precio &&
      //       x.courseLabelID === p.courseLabelID
      //   );

      //   if (index === -1) {
      //     newCart.push(p);
      //   } else {
      //     let isExisted = false;

      //     if (p.complementos.length > 0) {
      //       isExisted = isTheSameProductWithComplements(p, newCart[index]);

      //       index = isExisted ? index : -1;
      //     }

      //     if (index !== -1) {
      //       newCart[index].notas = p.notas;
      //       newCart[index].quantity += p.quantity;
      //       newCart[index].unidadesPagadas += p.unidadesPagadas;
      //       newCart[index].subtotal += p.subtotal;
      //     } else {
      //       newCart.push(p);
      //     }
      //   }
      // });

      // state.checkout.cart = newCart;

      var total_data = state.checkout.cart.map((p) => p.subtotal);
      var total = total_data.reduce((prev, next) => prev + next);


      state.checkout.total = total;
      state.checkout.subtotal = total;
    },
    splitAndUpdateProduct(state, action) {
      // action.payload: { id, index, precio, notas }
      // Decrements qty of original item by 1, adds a new qty=1 item with the given price/note
      const { id, index, precio, notas } = action.payload;

      const cartIndex = state.checkout.cart.findIndex(
        (p, i) => p.id === id && !p.old && i === index,
      );
      if (cartIndex === -1) return;
      const item = state.checkout.cart[cartIndex];

      const newQty = item.quantity - 1;
      if (newQty <= 0) {
        // Replace with the single unit carrying the new price/note
        state.checkout.cart.splice(cartIndex, 1, {
          ...item,
          quantity: 1,
          precio: Number(precio),
          notas,
          subtotal: Number(precio),
        });
      } else {
        // Update original: decrease qty by 1
        state.checkout.cart[cartIndex] = {
          ...item,
          quantity: newQty,
          subtotal: item.precio * newQty,
        };
        // Add new entry with the specific price/note
        state.checkout.cart.splice(cartIndex + 1, 0, {
          ...item,
          quantity: 1,
          precio: Number(precio),
          notas,
          subtotal: Number(precio),
        });
      }

      var total_data = state.checkout.cart.map((p) => p.subtotal);
      var total = total_data.reduce((prev, next) => prev + next);
      state.checkout.total = total;
      state.checkout.subtotal = total;
    },
    setSelectedProductoCart(state, action) {
      state.selectedProductoCart = action.payload;
    },
    cleanSelectedProductoCart(state, action) {
      state.selectedProductoCart = null;
    },
    haveComplementoSuccess(state, action) {
      state.haveComplemento = action.payload;
    },
    getComplementosSuccess(state, action) {
      state.complementos = action.payload;
    },
    setSelectedSubProductSuccess(state, action) {
      state.selectedSubProduct = action.payload;
    },
    setStepsSuccess(state, action) {
      state.complementSelection.steps = action.payload;
    },
    setCurrentStep(state, action) {
      state.complementSelection.currentStep = action.payload;
    },
    setComplementosSuccess(state, action) {
      state.complementSelection.complementos = action.payload;
    },
    onNextComplementStep(state, action) {
      state.complementSelection.currentStep += 1;
      if (
        state.complementSelection.currentStep ===
        state.complementSelection.steps.length
      ) {
        state.complementSelection.isComplete = true;
      } else {
        var productSelected = state.complementSelection.cart.find(
          (p) => p.step == state.complementSelection.currentStep,
        );
        if (productSelected != null) {
          if (productSelected.product != null) {
            state.complementSelection.selectedProduct =
              productSelected.product.id;
          }
        }
      }
    },
    onBackComplementStep(state, action) {
      state.complementSelection.currentStep -= 1;
      state.complementSelection.isComplete = false;
      state.complementSelection.selectedProduct =
        state.complementSelection.cart.find(
          (c) => c.step === state.complementSelection.currentStep,
        )?.product?.id;
    },
    onSpecificComplementStep(state, action) {
      state.complementSelection.currentStep = action.payload;
    },
    onAddComplementCart(state, action) {
      var product = action.payload.product;
      var step = action.payload.step;
      var exist = false;

      state.complementSelection.cart = state.complementSelection.cart.map(
        (c) => {
          if (c.step === step) {
            exist = true;
            if (c.product.precio !== undefined) {
              state.complementSelection.total =
                state.complementSelection.total -
                c.product.precio +
                product.precio;
            } else {
              state.complementSelection.total += product.precio;
            }

            return action.payload;
          } else {
            return c;
          }
        },
      );

      if (state.complementSelection.cart.length === 0 || !exist) {
        state.complementSelection.cart = [
          ...state.complementSelection.cart,
          action.payload,
        ];
        state.complementSelection.total += action.payload.product.precio;
      }
    },

    //cuando vuevo a seleccionar un producto seleccionado, este se elimina y al darle atras me da error
    onRemoveComplementCart(state, action) {
      state.complementSelection.cart = state.complementSelection.cart.map(
        (c) => {
          if (c.step === action.payload) {
            state.complementSelection.total -= c.product.precio;
            if (state.complementSelection.selectedProduct === c.product.id) {
              state.complementSelection.selectedProduct = -1;
            }
            return { ...c, product: {} };
          } else {
            return c;
          }
        },
      );
    },
    setSelectedComplementProduct(state, action) {
      state.complementSelection.selectedProduct = action.payload;
    },
    setLoad(state, action) {
      state.complementSelection.isLoad = action.payload;
    },
    cleanComplement(state, action) {
      state.complementSelection.cart = [];
      state.complementSelection.complementos = [];
      state.complementSelection.total = 0;
      state.complementSelection.steps = [];
      state.complementSelection.currentStep = 0;
      state.complementSelection.isComplete = false;
      state.complementSelection.selectedProduct = -1;
      state.complementSelection.isLoad = false;
    },

    setDeleteMode(state, action) {
      state.deleteMode = action.payload;
    },
    resetCheckoutToDelete(state, action) {
      state.checkoutToDelete = [];
    },
    addProductToDelete(state, action) {
      const product = action.payload;
      const isEmptyCart = state.checkoutToDelete.length === 0;
      var existed = false;

      if (isEmptyCart) {
        state.checkoutToDelete = [...state.checkoutToDelete, product];
      } else {
        state.checkoutToDelete = map(state.checkoutToDelete, (_product) => {
          var isExisted = isTheSameProduct(product, _product);

          if (isExisted) {
            existed = true;
            var subtotal =
              (_product.quantity + product.quantity) * _product.precio;
            return {
              ..._product,
              notas: product.notas,
              quantity: _product.quantity + product.quantity,
              subtotal: subtotal,
            };
          }
          return _product;
        });
      }

      if (!existed && !isEmptyCart) {
        state.checkoutToDelete = [product, ...state.checkoutToDelete];
      }
    },
    resetAllData(state, action) {
      state.isLoading = false;
      state.createdSuccess = false;
      state.updatedSuccess = false;
      state.error = false;
      state.menu = null;
      state.menus = [];
      state.products = [];
      state.frequentProducts = [];
      state.copas = [];
      state.product = null;
      state.sortBy = null;
      state.selectedComplemento = null;
      state.selectedIdComplemento = -1;
      state.selectedProduct = -1;
      state.selectedSubProduct = -1;
      state.unidadesSelectedProduct = -1;
      state.openComplementos = false;
      state.selectedCopa = null;
      state.categoryCreateOrder = [];
      state.selectedPage = 0;
      state.productPerPage = 5;
      state.lastTimeRequest = null;
      state.unidadesCopa = -1;
      state.filters = {
        category: [],
        priceRange: [0, 100],
        alergenos: [],
        bebida: "All",
        productName: "All",
      };

      state.checkout = {
        activeStep: 0,
        cart: [],
        oldCart: [],
        subtotal: 0,
        total: 0,
        discount: 0,
        shipping: 0,
        billing: null,
        idOrder: null,
        isPay: null,
        buttonPayClicked: null,
      };
      state.checkoutHistory = [];
      state.complementSelection = {
        cart: [],
        complementos: [],
        total: 0,
        steps: [],
        currentStep: 0,
        isComplete: false,
        selectedProduct: -1,
        isLoad: false,
      };
      state.isNotasOpen = false;
      state.nota_temp = "";
      state.images = [
        {
          name: "",
          data: "",
        },
      ];
      state.selectedProductoCart = null;
      state.haveComplemento = false;
      state.complementos = [];
      state.page = 0;
      state.filterName = "";
      state.checkoutEdit = {
        activeStep: 0,
        cart: [],
        oldCart: [],
        subtotal: 0,
        total: 0,
        discount: 0,
        shipping: 0,
        billing: null,
        idOrder: null,
        isPay: null,
        buttonPayClicked: null,
      };
      state.checkoutToDelete = [];
      state.deleteMode = false;
      state.isLoadingPayment = false;
      state.partialPayment = null;
      state.onEditComplementProduct = null;
      state.lastCategorySelectedWhenCreate = null;
      state.complementosValues = [];
    },

    // Cambia el curso (courseLabelID) de UN item del cart identificado
    // por su índice. Uso: resumen del MarchasFab permite corregir un
    // producto que se marcó con el curso equivocado ANTES de comandar.
    // Solo aplica a items pending (no `old`). Payload:
    //   { index: number, courseLabelID: number|null }
    changeCartItemCourse(state, action) {
      const { index, complementIndex, courseLabelID } = action.payload || {};
      if (typeof index !== "number" || index < 0) return;
      const item = state.checkout.cart[index];
      if (!item) return;
      if (item.old) return; // no tocar items ya comandados
      const nextCourse = courseLabelID ?? null;

      // Editar el curso de un COMPLEMENTO concreto dentro del padre.
      // El shape del complemento en el cart tiene el curso bajo varios
      // nombres según el origen (ver [[feedback-json-case-insensitive-collision]]);
      // sincronizamos los 3 para que cualquier lector — resumen del
      // FAB, payload al comandar, futuras vistas — lo encuentre.
      if (typeof complementIndex === "number" && complementIndex >= 0) {
        const comps = Array.isArray(item.complementos) ? item.complementos : [];
        if (complementIndex >= comps.length) return;
        const nextComps = comps.map((c, ci) =>
          ci === complementIndex
            ? {
                ...c,
                courseLabelID: nextCourse,
                courseLabelId: nextCourse,
                complementCourseLabelId: nextCourse,
              }
            : c,
        );
        const next = { ...item, complementos: nextComps };
        state.checkout.cart = state.checkout.cart.map((it, i) =>
          i === index ? next : it,
        );
        return;
      }

      // Cambio de curso del PADRE (comportamiento original).
      // Reemplazo por índice — SIN merge automático con otros items del
      // mismo curso. Cambiar el curso puede o no unificar filas idénticas
      // (el usuario lo verá en el resumen agrupado). Mantener el layout
      // predecible tras el edit.
      const next = { ...item, courseLabelID: nextCourse };
      state.checkout.cart = state.checkout.cart.map((it, i) =>
        i === index ? next : it,
      );
    },

    // Toggle "pre-marcha" por línea del cart. Cuando true, al comandar
    // el DTO envía MarchadoAt=now para esa línea → la cocina la ve como
    // MARCHADA en la primera comanda (sin necesidad de un segundo paso
    // por el panel Marchas). Solo tiene sentido en líneas con curso —
    // sin curso el flag es no-op en el backend.
    setCartItemPreMarch(state, action) {
      const { index, preMarch } = action.payload || {};
      if (typeof index !== "number" || index < 0) return;
      const item = state.checkout.cart[index];
      if (!item || item.old) return;
      const next = { ...item, preMarch: !!preMarch };
      state.checkout.cart = state.checkout.cart.map((it, i) =>
        i === index ? next : it,
      );
    },

    // Bulk toggle "pre-marcha" para TODAS las líneas del cart con curso
    // asignado (pending, no `old`). Botón "Marchar todos" en el toolbar
    // del cart — atajo al camarero para servicios express.
    setAllCartPreMarch(state, action) {
      const preMarch = !!action.payload;
      state.checkout.cart = state.checkout.cart.map((it) => {
        if (it.old) return it;
        if (it.courseLabelID == null) return it;
        return { ...it, preMarch };
      });
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  updatePaymentProducts,
  getProductSuccessById,
  cleanProduct,
  setLoad,
  handleChangeLastCategorySelectedWhenCreate,
  cleanComplement,
  setSelectedComplementProduct,
  onAddComplementCart,
  onRemoveComplementCart,
  onNextComplementStep,
  onBackComplementStep,
  onSpecificComplementStep,
  getCart,
  addCart,
  addInfoCart,
  resetCart,
  restoreCheckoutSnapshot,
  onGotoStep,
  onBackStep,
  onNextStep,
  deleteCart,
  createBilling,
  applyShipping,
  applyDiscount,
  applyLoyaltyRewardToCartLine,
  removeLoyaltyRewardFromCartLine,
  increaseQuantity,
  decreaseQuantity,
  sortByProducts,
  filterProducts,
  createProductSuccess,
  updateProductSuccess,
  createMenuSuccess,
  getMenuSuccess,
  getMenusSuccess,
  refreshProductSuccess,
  cleanCheckoutSuccess,
  setSelectedProductSuccess,
  setSelectedUnidadesSuccess,
  getCopasSuccess,
  copaSelectedSuccess,
  setUniadesCopaSelected,
  cleanCopaSuccess,
  selectedComplementoSuccess,
  setComplementosOpen,
  cleanSelected,
  filterCart,
  setOpenNotas,
  deleteProductSuccess,
  activateProductSuccess,
  addImagesSuccess,
  resetImages,
  removeSpecificImage,
  addProductoSuccess,
  addProductoSimpleSuccess,
  updateProductoSuccess,
  updateProductAsAdmin,
  splitAndUpdateProduct,
  setSelectedProductoCart,
  cleanSelectedProductoCart,
  haveComplementoSuccess,
  getComplementosSuccess,
  addComplementoSuccess,
  setSelectedSubProductSuccess,
  setStepsSuccess,
  setCurrentStep,
  setComplementosSuccess,
  changePageSuccess,
  cleanImagesSuccess,
  changeProductsPerPage,
  addOldCart,
  comandarOrder,
  optimisticComandarStart,
  optimisticComandarCommit,
  optimisticComandarRollback,
  saveHistoryCheckout,
  setHistoryDraftCart,
  pruneCheckoutHistory,
  cleanHistoryMesa,
  syncCartCheckout,
  syncCartCheckoutMany,
  addCartMany,
  resetCartOrder,
  handleNextPage,
  previousPage,
  resetPage,
  handleFilterName,
  setProductCategoryFilter,
  updateCategoryOrderUpdate,
  changeOrderCategory,
  addProductToDelete,
  resetCheckoutToDelete,
  setDeleteMode,
  decreaseQuantityDelete,
  cleanOldCart,
  // Expuestas para hidratación desde bootstrap (evita re-fetch).
  getProductsSuccess,
  getFrequentProductsSuccess,
  changeIsLoadingPayment,
  setEditComplementProduct,
  addEditCart,
  onAddPartialpayment,
  cleanProducts,
  syncCartCheckoutComplete,
  addOldCartAndClean,
  resetAllData,
  changeCartItemCourse,
  setCartItemPreMarch,
  setAllCartPreMarch,
  replaceMenuLines,
  setActiveComensal,
  setComensales,
  moveProductsToComensal,
  setSubtotalPorComensal,
  setCartLinePartialFlag,
  applyFraccionarResult,
  applyComensalDiffByCounter,
  reassignComensalToUnassigned,
} = slice.actions;

// ────────────────────────────────────────────────────────────────
// 2026-09-03 · Split-by-comensal — thunks para el CRUD backend
// ────────────────────────────────────────────────────────────────

/**
 * Refresca la lista de comensales de una sesión y la cachea en Redux.
 * El caller la llama al abrir/cambiar de mesa. Idempotente.
 */
export function fetchComensales(idSession) {
  return async (dispatch) => {
    // Guard mesa libre: sin sesión activa (GUID vacío o null/undefined)
    // → no hay comensales que traer. El backend además tira 500 si le
    // pasas Guid.Empty. Simplemente vaciamos la lista local.
    const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";
    if (!idSession || idSession === EMPTY_GUID) {
      dispatch(setComensales([]));
      return [];
    }
    try {
      const res = await axios.get(`dinerSplit/session/${idSession}/comensales`);
      dispatch(setComensales(res.data || []));
      return res.data;
    } catch (err) {
      // Si la sesión no existe / está cerrada, dejamos la lista vacía.
      if (process.env.NODE_ENV !== "production") console.error(err);
      dispatch(setComensales([]));
    }
  };
}

export function addComensal(idSession, nombre) {
  return async (dispatch) => {
    markLocalDinerSplitAction(idSession);
    const res = await axios.post(
      `dinerSplit/session/${idSession}/comensales`,
      { nombre: nombre || null },
    );
    dispatch(setComensales(res.data || []));
    // 2026-09-03 · Backend sincroniza `Mesa.Comensales` — el chip del
    // nº de comensales en la mesa activa depende de este refetch.
    dispatch(getMesas({ fresh: true }));
    return res.data;
  };
}

export function renameComensal(idSession, numero, nombre) {
  return async (dispatch) => {
    markLocalDinerSplitAction(idSession);
    const res = await axios.patch(
      `dinerSplit/session/${idSession}/comensales/${numero}`,
      { nombre: nombre || null },
    );
    dispatch(setComensales(res.data || []));
    return res.data;
  };
}

export function deleteComensal(idSession, numero) {
  return async (dispatch, getState) => {
    markLocalDinerSplitAction(idSession);
    const res = await axios.delete(
      `dinerSplit/session/${idSession}/comensales/${numero}`,
    );
    dispatch(setComensales(res.data || []));
    dispatch(getMesas({ fresh: true }));
    // 2026-09-04 · Optimismo local: mover las líneas del cart y de
    // los orders del comensal borrado al bucket C0 al instante. El
    // backend ya hizo el ExecuteUpdateAsync equivalente; sin esta
    // mutación local el cart seguía pintando las líneas con el chip
    // del comensal borrado hasta la próxima recarga.
    dispatch(slice.actions.reassignComensalToUnassigned({ numero }));
    // Mismo optimismo en state.orders — el sidebar del PaymentDialog
    // depende de orders para pintar el bucket por comensal.
    try {
      const ordersState = getState().order?.orders || [];
      const entries = [];
      ordersState.forEach((o) => {
        (o.productos || []).forEach((p) => {
          if ((Number(p.idComensal) || 0) === Number(numero)) {
            entries.push({
              pedidoID: o.id,
              counterProductID: p.idNumPedido ?? p.counterProductId,
              complementoID: p.idComplemento ?? -1,
            });
          }
        });
      });
      if (entries.length > 0) {
        dispatch(optimisticSetComensal({ entries, newComensal: 0 }));
      }
    } catch (_) { /* best-effort */ }
    // Recarga defensiva: si por race el broadcast SignalR no llega o
    // el guard lo bloqueó, forzamos SyncCart de la mesa activa para
    // sincronizar cart + orders con backend después del delete.
    try {
      const mesa = getState().mesas?.mesa;
      const activeSession = mesa?.sessionId || mesa?.idSession;
      if (mesa?.id && activeSession === idSession) {
        dispatch(SyncCart(mesa.id, idSession));
      }
    } catch (_) { /* best-effort */ }
    return res.data;
  };
}

/**
 * Mueve N líneas ya comandadas a otro comensal en el backend + refresh
 * de la sesión. Para líneas del cart LOCAL usa el reducer
 * moveProductsToComensal (síncrono, sin backend).
 * `newComensal=0` = sin asignar.
 */
/**
 * 2026-09-03 · Variante multi-target del bulk mover. Cada entry ya lleva
 * su `newComensal` — el backend soporta esto de nativo (Entries[N] con
 * NewComensal por entrada). Sustituye al patrón `N × moverProductos`
 * (una llamada por comensal target) que hacía el FraccionarDialog al
 * guardar: con 8 comensales = 8 requests HTTP. Aquí una sola llamada.
 *
 * Además hace mutación optimista EXACTA por (pedidoID, counterProductID,
 * complementoID, lineIndex) → sustituye el idComensal en `state.orders`
 * y en cart lines matching por PK. Sin colisión con productos iguales
 * de distintos comensales (como sí sufre `moverProductosComensalBackend`
 * por matchear con producto+precio).
 */
export function moverProductosComensalMulti(entries) {
  return async (dispatch, getState) => {
    if (!Array.isArray(entries) || entries.length === 0) {
      return { affected: 0, subtotalPorComensal: {} };
    }
    // Marca del broadcast rebound: la sesión afectada la deducimos del
    // mesa activa en el store (el thunk suele llamarse desde la mesa
    // activa; si no, el marcado no hace daño — solo previene un
    // SyncCart redundante que iba a ejecutarse igual).
    try {
      const activeSession =
        getState().mesas?.mesa?.sessionId || getState().mesas?.mesa?.idSession;
      markLocalDinerSplitAction(activeSession);
    } catch (_) { /* best-effort */ }
    // 2026-09-03 · CORRECCIÓN del pedidoID por counterProductID.
    // Bug: el cart local agrupa líneas del mismo producto+precio+
    // comensal en 1 sola entrada con `counterProductId: [array]`, pero
    // guarda un solo `idPedido` (el del PRIMER counter que entró). Si
    // el array contiene counters de PEDIDOS DISTINTOS (típico cuando
    // hay 2 comandas separadas), el `pedidoID` del entry apunta al
    // pedido equivocado → PK no matchea → backend no actualiza nada
    // → optimismo en `orders` tampoco encuentra el order → UI mira al
    // vacío. Fix: lookup counterProductID → pedidoID vía state.orders.
    const state = getState();
    const orders = state.order?.orders || [];
    const pedidoByCounter = new Map();
    orders.forEach((o) => {
      (o.productos || []).forEach((p) => {
        const cid = p.idNumPedido ?? p.counterProductId;
        if (cid != null && o.id != null && !pedidoByCounter.has(cid)) {
          pedidoByCounter.set(cid, o.id);
        }
      });
    });
    const fixed = entries.map((e) => ({
      ...e,
      pedidoID: pedidoByCounter.get(e.counterProductID) ?? e.pedidoID,
    }));
    const body = {
      entries: fixed.map((e) => ({
        pedidoID: e.pedidoID,
        productoID: e.productoID,
        complementoID: e.complementoID ?? -1,
        counterProductID: e.counterProductID,
        lineIndex: e.lineIndex ?? 0,
        newComensal: Number(e.newComensal) || 0,
        // 2026-09-04 · Split-in-place. Null = mover fila entera
        // (comportamiento clásico). Valor <row.Unidades = backend
        // divide la fila en dos (original queda con `Unidades -=`,
        // hija nueva con `Unidades = unidadesToMove, IdComensal =
        // NewComensal, LineIndex = max+1`).
        unidadesToMove: e.unidadesToMove ?? null,
        // Comensal antiguo — validación defensiva backend: si la
        // fila localizada por PK no tiene este idComensal (otro
        // dispositivo ya la movió, o LineIndex stale), la entry
        // se descarta en vez de corromper el estado.
        sourceComensal:
          e.sourceComensal ?? e.idComensalPrev ?? null,
      })),
    };
    // Optimismo en state.orders — un pase por entry con su newComensal.
    // También mutamos el cart por counterProductID exacto para que el
    // sidebar del checkout refleje el cambio de idComensal al instante.
    try {
      dispatch(applyComensalDiffByCounter({
        assignments: fixed.map((e) => ({
          counterProductID: e.counterProductID,
          complementoID: e.complementoID ?? -1,
          newComensal: Number(e.newComensal) || 0,
        })),
      }));
      const byTarget = new Map();
      fixed.forEach((e) => {
        const t = Number(e.newComensal) || 0;
        if (!byTarget.has(t)) byTarget.set(t, []);
        byTarget.get(t).push(e);
      });
      byTarget.forEach((list, t) => {
        dispatch(optimisticSetComensal({ entries: list, newComensal: t }));
      });
    } catch (_) { /* best-effort */ }
    const res = await axios.post("dinerSplit/mover-productos", body);
    if (res.data?.subtotalPorComensal) {
      dispatch(setSubtotalPorComensal(res.data.subtotalPorComensal));
    }
    // 2026-09-04 · Refresh de cart + orders tras el mover — sin esto
    // los LineIndex nuevos creados por el backend (split-in-place) no
    // llegan al frontend y la siguiente operación envía `lineIndex:0`
    // apuntando a filas ya consumidas → backend no encuentra nada
    // → operación silenciosa. Con SyncCart el cart local queda con
    // los `lineDetails` frescos del DTO backend.
    try {
      const mesa = getState().mesas?.mesa;
      const sess = mesa?.sessionId || mesa?.idSession;
      if (mesa?.id && sess) {
        dispatch(SyncCart(mesa.id, sess));
      }
    } catch (_) { /* best-effort */ }
    return res.data || { affected: 0, subtotalPorComensal: {} };
  };
}

export function moverProductosComensalBackend(entries, newComensal) {
  return async (dispatch, getState) => {
    if (!Array.isArray(entries) || entries.length === 0) return { affected: 0 };
    try {
      const activeSession =
        getState().mesas?.mesa?.sessionId || getState().mesas?.mesa?.idSession;
      markLocalDinerSplitAction(activeSession);
    } catch (_) { /* best-effort */ }
    // 2026-09-03 · Lookup counterProductID → pedidoID en state.orders
    // (ver comentario extenso en `moverProductosComensalMulti`). Sin
    // esto el pedidoID que viene del cart line agrupado puede apuntar
    // al pedido equivocado cuando el array counterProductId contiene
    // ids de pedidos distintos.
    const state = getState();
    const orders = state.order?.orders || [];
    const pedidoByCounter = new Map();
    orders.forEach((o) => {
      (o.productos || []).forEach((p) => {
        const cid = p.idNumPedido ?? p.counterProductId;
        if (cid != null && o.id != null && !pedidoByCounter.has(cid)) {
          pedidoByCounter.set(cid, o.id);
        }
      });
    });
    const fixed = entries.map((e) => ({
      ...e,
      pedidoID: pedidoByCounter.get(e.counterProductID) ?? e.pedidoID,
    }));
    const body = {
      entries: fixed.map((e) => ({
        pedidoID: e.pedidoID,
        productoID: e.productoID,
        complementoID: e.complementoID ?? -1,
        counterProductID: e.counterProductID,
        lineIndex: e.lineIndex ?? 0,
        newComensal: Number(newComensal) || 0,
        // Frontend envía unidadesToMove por entry (ver
        // buildMoveEntries) — con LineIndex real desde lineDetails,
        // ya no se mueve la fila entera por default.
        unidadesToMove: e.unidadesToMove ?? null,
        // Comensal antiguo — validación defensiva backend.
        sourceComensal:
          e.sourceComensal ?? e.idComensalPrev ?? null,
      })),
    };
    // 2026-09-03 · Mutación optimista PRECISA por counterProductID
    // (splittea la línea del cart si algunas unidades cambian y otras
    // no). Reemplaza al `moveProductsToComensal` viejo que matcheaba
    // por producto+precio → colisionaba con productos iguales de
    // otros comensales y movía TODAS las unidades matching.
    try {
      dispatch(applyComensalDiffByCounter({
        assignments: fixed.map((e) => ({
          counterProductID: e.counterProductID,
          complementoID: e.complementoID ?? -1,
          newComensal: Number(newComensal) || 0,
        })),
      }));
      dispatch(optimisticSetComensal({ entries: fixed, newComensal }));
    } catch (_) { /* optimista best-effort */ }
    const res = await axios.post("dinerSplit/mover-productos", body);
    if (res.data?.subtotalPorComensal) {
      dispatch(setSubtotalPorComensal(res.data.subtotalPorComensal));
    }
    // Ver comentario en moverProductosComensalMulti — refresh
    // automático tras el mover para que las lineDetails del cart
    // reflejen los splits del backend.
    try {
      const mesa = getState().mesas?.mesa;
      const sess = mesa?.sessionId || mesa?.idSession;
      if (mesa?.id && sess) {
        dispatch(SyncCart(mesa.id, sess));
      }
    } catch (_) { /* best-effort */ }
    return res.data || { affected: 0, subtotalPorComensal: {} };
  };
}

// ----------------------------------------------------------------------

export function getProducts(getAll, { force = false } = {}) {
  return async (dispatch) => {
    if (!getAll) getAll = false;
    // Catálogo pesado (1.3 MB medido en el HAR). Ya viene en el bootstrap.
    // Con staleGuard evitamos las 2-3 recargas por sesión que ocurrían al
    // navegar entre carta/producto/tpv.
    if (!force && staleGuard(`productos/${getAll}`)) return;
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`productos/${getAll}`);
      dispatch(slice.actions.getProductsSuccess(response.data.products));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// `fresh: true` → GET nuevo aunque haya uno idéntico en vuelo (tras una
// mutación propia: unirse a un GET que salió antes devolvería lo viejo).
export function SyncCart(mesaId, sessionId, { fresh = false } = {}) {
  return async (dispatch) => {
    try {
      const userId = window.localStorage.getItem("userId");

      // 2026-09-04 · SyncCart NO destruye el cart entero — solo borra
      // los OLD viejos y los repuebla con lo que trae el backend. Los
      // NEW (no comandados) del camarero se quedan INTACTOS en el
      // store. El intento previo de snapshot+resetCart+re-inyectar
      // los NEW causaba filas "fantasma": si el user cambiaba
      // activeComensal entre snapshot y re-inject, los prev retenían
      // idComensal viejo y aparecían como líneas huérfanas junto a
      // los nuevos con idComensal actual. Ahora `resetOldCartOnly`
      // preserva los NEW en su estado real sin re-mergear.
      await axios
        .get(`pedidos/${userId}/${mesaId}/${sessionId}`, {
          ...(fresh ? { skipDedupe: true } : {}),
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "Access-Control-Allow-Origin": "*",
          },
        })
        .then((response) => {
          // 2026-09-08 · SyncCart también refresca `state.order.orders`
          // — antes solo actualizaba `state.checkout.cart`. Tras
          // MoverProductos, el optimismo de `optimisticSetComensal`
          // dejaba orders con idComensal correcto pero SIN
          // lineDetails/CounterProductID frescos del backend. Al abrir
          // el PaymentDialog leyendo `state.order.orders` se veían las
          // líneas sin comensal (o con datos stale) hasta que llegaba
          // el próximo `getOrdersBySession`. Reusamos la misma
          // respuesta HTTP (mismo endpoint que `getOrders`) para no
          // hacer una petición extra.
          // Fase 3A · un solo batch de renders: antes un dispatch por línea
          // (N renders y reconstrucción O(N²) del cart). Mismo resultado.
          const items = [];
          response.data.orders
            .filter((p) => p.approved)
            .forEach((p) => {
              p.productos.forEach((product) => {
                items.push({
                  ...product,
                  quantity: product.unidades,
                  subtotal: product.precio * product.unidades,
                  notas: product.notas,
                  complementos: product.complementos,
                  servido: p.servido,
                  old: true,
                });
              });
            });
          batch(() => {
            dispatch(getOrdersSuccess(response.data.orders));
            dispatch(slice.actions.resetOldCartOnly());
            dispatch(slice.actions.syncCartCheckoutMany(items));
          });
        });
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function deleteProduct(productId) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.deleteProductSuccess(productId));
      await axios.delete(`productos/${productId}`);
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function activateProduct(productId) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.activateProductSuccess(productId));
      await axios.put(`productos/activate/${productId}`);
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function setSelectedProduct(id) {
  return async (dispatch) => {
    dispatch(slice.actions.setSelectedProductSuccess(id));
  };
}

export function setSelectedComplemento(complemento) {
  return async (dispatch) => {
    dispatch(slice.actions.selectedComplementoSuccess(complemento));
  };
}

export function setSelectedUnidades(unidades) {
  return async (dispatch) => {
    dispatch(slice.actions.setSelectedUnidadesSuccess(unidades));
  };
}

export function getFrequentProducts() {
  return async (dispatch) => {
    try {
      const response = await axios.get(`productos/GetFrequentProducts`);
      dispatch(slice.actions.getFrequentProductsSuccess(response.data.products));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getCopas(user) {
  return async (dispatch) => {
    try {
      const response = await axios.get(`productos/copas/restaurante/${user}`);
      dispatch(slice.actions.getCopasSuccess(response.data.products));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function setProductoNota(nota) {
  return async (dispatch) => {
    try {
      dispatch(
        slice.actions.setOpenNotas({
          id: nota.id,
          isOpen: nota.isOpen,
          nota: nota.nota,
        }),
      );
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function SetCopa(copa) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.copaSelectedSuccess(copa));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function SetUnidadesCopa(unidades) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.setUniadesCopaSelected(unidades));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function CleanSelectedCopas() {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.cleanCopaSuccess());
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getProduct(restaurante, name) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(
        `productos/product/${restaurante}/${name}`,
      );
      dispatch(slice.actions.getProductSuccess(response.data.product));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getComplementos(restaurante, getAll) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      if (!getAll) getAll = false;

      const response = await axios.get(
        `productos/complementos/${restaurante}/${getAll}`,
      );
      dispatch(
        slice.actions.getComplementosSuccess(response.data.complementos),
      );
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function addProducto(producto) {
  return async (dispatch) => {
    try {
      const response = await axios.post("productos", JSON.stringify(producto), {
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "Access-Control-Allow-Origin": "*",
        },
      });
      dispatch(slice.actions.addProductoSuccess(response.data.product));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function duplicateProducto(productId) {
  return async (dispatch) => {
    try {
      const response = await axios.post(`productos/duplicate/${productId}`, {
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "Access-Control-Allow-Origin": "*",
        },
      });
      dispatch(slice.actions.addProductoSuccess(response.data.product));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function updateProducto(producto) {
  return async (dispatch) => {
    try {
      const response = await axios.put("productos", JSON.stringify(producto), {
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "Access-Control-Allow-Origin": "*",
        },
      });

      dispatch(slice.actions.updateProductoSuccess(response.data));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function updateProduct(prod) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.put("products/product", {
        prod,
      });
      const { product } = response.data;
      dispatch(slice.actions.updateProductSuccess(product));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function createProduct(prod) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post("productos/add", {
        prod,
      });
      const { product } = response.data;
      dispatch(slice.actions.createProductSuccess(product));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getMenus() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get("menus");
      dispatch(slice.actions.getProductsSuccess(response.data.products));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getMenu(name) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get("menus/menu", {
        params: { name },
      });
      dispatch(slice.actions.getProductSuccess(response.data.product));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function createMenu(prod) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post("menus/new", {
        prod,
      });
      const { product } = response.data;
      dispatch(slice.actions.createProductSuccess(product));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}
